import React from 'react';
import { ArrowLeft, Shield, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

export const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-purple-600 hover:text-purple-500 font-medium mb-4"
          >
            <ArrowLeft size={16} className="mr-1" />
            Back to Home
          </Link>
          
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm rounded-full px-6 py-2 mb-6 border border-purple-200">
              <Shield size={20} className="text-purple-600" />
              <span className="text-purple-700 font-medium">Legal Terms</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
            <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          {/* Important Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8">
            <div className="flex items-start space-x-3">
              <AlertTriangle size={24} className="text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-amber-800 mb-2">Important Notice</h3>
                <p className="text-amber-700 text-sm leading-relaxed">
                  Please read these Terms of Service carefully before using Manivo. By accessing or using our service, 
                  you agree to be bound by these terms. If you disagree with any part of these terms, you may not use our service.
                </p>
              </div>
            </div>
          </div>

          <div className="prose prose-lg max-w-none space-y-8">
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                By accessing and using Manivo ("the Service", "our Service", "the Platform"), operated by Manivo Inc. 
                ("we", "us", "our", "the Company"), you accept and agree to be bound by the terms and provision of this agreement.
              </p>
              <p className="text-gray-700 leading-relaxed">
                These Terms of Service ("Terms") constitute a legally binding agreement between you and Manivo Inc. 
                Your use of the Service is also governed by our Privacy Policy.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Manivo is a digital platform that provides manifestation coaching, guidance, and educational content. 
                Our services include but are not limited to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>AI-powered manifestation coaching conversations</li>
                <li>Daily manifestation cards and guidance</li>
                <li>Educational content about manifestation techniques</li>
                <li>Progress tracking and analytics</li>
                <li>Community features and resources</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                The Service is intended for entertainment, educational, and personal development purposes only.
              </p>
            </section>

            {/* Section 3 CRITICAL LIABILITY DISCLAIMER */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. NO GUARANTEES OR PROMISES</h2>
              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-4">
                <p className="text-red-800 font-semibold mb-3">
                  IMPORTANT: MANIVO MAKES NO REPRESENTATIONS, WARRANTIES, OR GUARANTEES OF ANY KIND REGARDING THE EFFECTIVENESS OF MANIFESTATION TECHNIQUES OR OUTCOMES.
                </p>
                <p className="text-red-700 leading-relaxed mb-3">
                  <strong>YOU ACKNOWLEDGE AND AGREE THAT:</strong>
                </p>
                <ul className="list-disc list-inside text-red-700 space-y-2">
                  <li>Manifestation is a subjective practice with no scientifically proven results</li>
                  <li>We do not guarantee that using our Service will result in any specific outcomes</li>
                  <li>Any results or experiences are entirely individual and may vary significantly</li>
                  <li>The effectiveness of manifestation techniques is not supported by scientific evidence</li>
                  <li>No outcomes, whether positive or negative, can be attributed to our Service</li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. USER RESPONSIBILITIES</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                By using Manivo, you agree that:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>You are solely responsible for your own actions, decisions, and their consequences</li>
                <li>You will not rely solely on our Service for important life decisions</li>
                <li>You understand that our Service is for entertainment and educational purposes only</li>
                <li>You will use the Service in accordance with all applicable laws and regulations</li>
                <li>You will not hold Manivo liable for any outcomes or lack thereof</li>
                <li>You are of legal age to enter into this agreement in your jurisdiction</li>
              </ul>
            </section>

            {/* Section 5 COMPREHENSIVE LIABILITY LIMITATION */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. LIMITATION OF LIABILITY</h2>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-4">
                <p className="text-gray-800 font-semibold mb-3">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, MANIVO SHALL NOT BE LIABLE FOR ANY DAMAGES OF ANY KIND, INCLUDING BUT NOT LIMITED TO:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                  <li>Direct, indirect, incidental, special, consequential, or punitive damages</li>
                  <li>Loss of profits, revenue, data, or business opportunities</li>
                  <li>Personal injury, emotional distress, or psychological harm</li>
                  <li>Financial losses of any kind</li>
                  <li>Relationship or personal issues</li>
                  <li>Failed manifestations or unmet expectations</li>
                  <li>Any negative outcomes claimed to be related to using our Service</li>
                  <li>Decisions made based on our Service content</li>
                </ul>
                <p className="text-gray-700 font-medium">
                  Our total liability, if any, shall not exceed the amount you paid for the Service in the 30 days preceding the claim.
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. DISCLAIMER OF WARRANTIES</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Merchantability or fitness for a particular purpose</li>
                <li>Non-infringement of third-party rights</li>
                <li>Accuracy, completeness, or reliability of content</li>
                <li>Uninterrupted or error-free operation</li>
                <li>Results or outcomes from using the Service</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. INDEMNIFICATION</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify, defend, and hold harmless Manivo, its officers, directors, employees, agents, 
                and affiliates from and against any and all claims, damages, losses, costs, and expenses (including attorney's fees) 
                arising from or relating to your use of the Service, your violation of these Terms, or your violation of any rights of another.
              </p>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. PAYMENT AND SUBSCRIPTIONS</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Certain features of our Service require payment. By subscribing to a paid plan:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>You authorize us to charge your payment method for the selected subscription</li>
                <li>Subscriptions automatically renew unless cancelled</li>
                <li>Refunds are provided at our sole discretion</li>
                <li>Payment does not guarantee any specific results or outcomes</li>
                <li>You acknowledge that you are paying for access to the Service, not for guaranteed results</li>
              </ul>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. INTELLECTUAL PROPERTY</h2>
              <p className="text-gray-700 leading-relaxed">
                All content, features, and functionality of the Service are owned by Manivo and protected by copyright, 
                trademark, and other intellectual property laws. You may not copy, modify, distribute, sell, or lease any part of our Service.
              </p>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. TERMINATION</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to terminate or suspend your access to the Service at any time, with or without cause, 
                and with or without notice. Upon termination, your right to use the Service will cease immediately.
              </p>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. GOVERNING LAW</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of [Your State/Country], 
                without regard to its conflict of law provisions. Any disputes shall be resolved in the courts of [Your Jurisdiction].
              </p>
            </section>

            {/* Section 12 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. CHANGES TO TERMS</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify users of any material changes by 
                posting the new Terms on this page. Your continued use of the Service after such modifications constitutes 
                your acceptance of the updated Terms.
              </p>
            </section>

            {/* Section 13 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. SEVERABILITY</h2>
              <p className="text-gray-700 leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited 
                or eliminated to the minimum extent necessary so that these Terms will otherwise remain in full force and effect.
              </p>
            </section>

            {/* Section 14 Final Warning */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. FINAL ACKNOWLEDGMENT</h2>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                <p className="text-purple-800 font-semibold mb-3">
                  BY USING MANIVO, YOU EXPLICITLY ACKNOWLEDGE THAT:
                </p>
                <ul className="list-disc list-inside text-purple-700 space-y-2">
                  <li>You understand manifestation is not scientifically proven</li>
                  <li>You assume all risks associated with using our Service</li>
                  <li>You will not hold Manivo responsible for any outcomes or lack thereof</li>
                  <li>You are using the Service for entertainment and educational purposes only</li>
                  <li>You have read, understood, and agree to these Terms in their entirety</li>
                </ul>
              </div>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. CONTACT INFORMATION</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700">
                  Email: legal@manivo.com<br />
                  Address: [Your Business Address]
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}; 